// ============ BUBBLE WATCH ============
//
// Two centuries of US market manias on one panel: how concentrated each one got
// before it broke, what happened next, the mega-IPO tell, the four gauges that
// say how stretched today is, and the liquidity that fuels it.
//
// The page is deliberately split into two kinds of number and labels which is
// which. The liquidity dials are live from FRED and carry their own observation
// date. The valuation gauges and every historical figure are hand-curated from
// the study and carry its compile date. Mixing them silently would be the easy
// thing and the dishonest one - a reader who cannot tell a live reading from a
// figure typed in August cannot judge either.
//
// Source: NOPOLY / nopoly.ai, after BofA Global Investment Strategy, Bianco
// Research and Adam Tooze's Chartbook #396.
import React, { useState, useEffect } from 'react';
import { bubbleWatch, type BubbleView, type BubbleDial } from '../services/api';
import { useT, useLang } from '../lib/i18n';

const n1 = (x: number | null | undefined, d = 1) => (x == null ? '—' : Number(x).toFixed(d));
const sign = (x: number | null | undefined, d = 1) =>
  x == null ? '—' : (x >= 0 ? '+' : '') + Number(x).toFixed(d);

export function BubbleWatchCard() {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [b, setB] = useState<BubbleView | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'locked' | 'anon' | 'error'>('loading');
  const [tab, setTab] = useState<'gauge' | 'history' | 'ipo' | 'concentration' | 'anatomy'>('gauge');

  useEffect(() => {
    let alive = true;
    bubbleWatch().then(r => {
      if (!alive) return;
      setState(r.state);
      if (r.state === 'ok') setB(r.data);
    }).catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, []);

  // Refused for lack of a plan: show the shape of what is behind the paywall.
  // MemberGate blurs whatever this renders, and a blurred empty box sells
  // nothing - the reader has to be able to see there is something worth buying.
  if (state === 'locked' || state === 'anon') return <BubbleTeaser t={t} anon={state === 'anon'} />;
  if (state !== 'ok' || !b) return null;

  const L = b.liquidity;
  const v = b.verdict;
  const verdictText = v.label === 'loose'
    // "หลวม" is the literal translation of loose and reads as a loose screw in
    // Thai. The money-market terms are ผ่อนคลาย and ตึงตัว.
    ? t('ผ่อนคลาย — เงินยังล้นระบบ', 'LOOSE — the tank is full')
    : v.label === 'mixed' ? t('ก้ำกึ่ง — สัญญาณผสม', 'MIXED') : t('ตึงตัว — เงินในระบบกำลังหด', 'TIGHT — fuel is draining');

  const TABS: { k: typeof tab; th: string; en: string }[] = [
    { k: 'gauge', th: 'มาตรวัด', en: 'Gauges' },
    { k: 'concentration', th: 'ความกระจุกตัว', en: 'Concentration' },
    { k: 'history', th: 'ประวัติวิกฤต', en: 'Crash history' },
    { k: 'ipo', th: 'สัญญาณ IPO', en: 'The IPO tell' },
    { k: 'anatomy', th: 'กายวิภาคฟองสบู่', en: 'Anatomy' },
  ];

  return (
    <div className="mm-bubble">
      <div className="mm-bubble-head">
        <div>
          <b>{t('Bubble Watch · เฝ้าระวังฟองสบู่', 'Bubble Watch')}</b>
          <div className="dim mm-bubble-lede">
            {t('วิกฤตตลาดสหรัฐทุกครั้งในหน้าเดียว — ความกระจุกตัว มูลค่า และระดับความคลั่ง เทียบกับหนึ่งศตวรรษของการพังทลาย หกครั้งในสองศตวรรษที่ธีมเดียวกลืนตลาดทั้งตลาด ทุกครั้งพีคที่ 36–63% แล้วก็แตก — AI Big 10 ตอนนี้อยู่ที่ 40%',
              'Every US market crisis on one page — concentration, valuation and mania gauges against a century of crashes. Six times in two centuries one theme swallowed the market, each peaking between 36% and 63% before it broke. The AI Big 10 just reached 40%.')}
          </div>
        </div>
        <div className={`mm-bubble-verdict ${v.label}`}>
          <span className="dim mono">{t('สภาพคล่องดอลลาร์', 'USD liquidity')}</span>
          <b>{verdictText}</b>
          <span className="dim mono">{v.score}/{v.of} {t('สัญญาณ', 'signals')}</span>
        </div>
      </div>

      {/* Fuel first. Every bubble runs on it, and unlike the valuation gauges
          these four move every week - so they are the live half of the page. */}
      <div className="mm-bubble-fuel">
        <Dial label={t('ปริมาณเงิน M2', 'M2 money supply')} d={L.m2}
          fmt={x => '$' + (x / 1000).toFixed(1) + 'T'}
          sub={L.m2?.changePct != null ? sign(L.m2.changePct, 1) + '% ' + t('จากปีก่อน', 'YoY') : null}
          good={(L.m2?.changePct ?? 0) > 0} t={t} />
        <Dial label={t('ดอกเบี้ย Fed', 'Fed funds')} d={L.fedFunds}
          fmt={x => x.toFixed(2) + '%'}
          sub={L.fedFunds?.peak != null
            ? t('ลงจากพีค ' + L.fedFunds.peak.toFixed(2) + '%', 'down from ' + L.fedFunds.peak.toFixed(2) + '% peak')
            : null}
          good={(L.fedFunds?.fromPeak ?? 0) <= -0.25} t={t} />
        <Dial label={t('งบดุล Fed', 'Fed balance sheet')} d={L.balanceSheet}
          fmt={x => '$' + (x / 1e6).toFixed(2) + 'T'}
          sub={L.balanceSheet?.changePct != null ? sign(L.balanceSheet.changePct, 1) + '% ' + t('จากปีก่อน', 'YoY') : null}
          good={(L.balanceSheet?.changePct ?? 0) > 0} t={t} />
        <Dial label={t('เงินฝากข้ามคืนที่ Fed (RRP)', 'Reverse repo')} d={L.reverseRepo}
          fmt={x => (x < 1 ? '≈$0' : '$' + x.toFixed(0) + 'B')}
          sub={t('ลดจาก $2.55T เมื่อปี 2022 จนเกือบหมด — เงินก้อนนั้นไหลกลับเข้าตลาด', 'drained from $2.55T in 2022 — that cash went back to markets')}
          good={(L.reverseRepo?.value ?? 1e9) < 100} t={t} />
      </div>
      <div className="dim mm-bubble-note">
        {t('สี่ตัวนี้ดึงสดจาก FRED ทุกครั้งที่เปิดหน้า · เงินที่ล้นระบบคือเหตุผลที่ของ "แพง" แพงต่อไปได้อีก และมันก็เป็นตัวเจาะฟองสบู่ในทางกลับกันด้วย — ปี 1929, ญี่ปุ่นปี 1989, ปี 2000 และปี 2022 ล้วนจบลงตอนเงินตึงตัว สิ่งที่ต้องเฝ้าจึงไม่ใช่ราคา แต่เป็นปริมาณเงินที่หล่อเลี้ยงมันอยู่',
          'These four are pulled live from FRED on every load. Loose liquidity is why expensive can keep getting more expensive — and it is also the classic bubble-popper in reverse: 1929, Japan 1989, 2000 and 2022 all ended when money got tight. The number to watch is not the price, it is the fuel.')}
      </div>

      <div className="mm-bubble-tabs">
        {TABS.map(x => (
          <button key={x.k} className={tab === x.k ? 'on' : ''} onClick={() => setTab(x.k)}>
            {en ? x.en : x.th}
          </button>
        ))}
      </div>

      {tab === 'gauge' && <Gauges b={b} t={t} en={en} />}
      {tab === 'concentration' && <Concentration b={b} t={t} en={en} />}
      {tab === 'history' && <CrashHistory b={b} t={t} en={en} />}
      {tab === 'ipo' && <IpoTell b={b} t={t} en={en} />}
      {tab === 'anatomy' && <Anatomy b={b} t={t} en={en} />}

      <div className="dim mm-bubble-foot">
        {t('งานวิจัยเพื่อการศึกษา ไม่ใช่คำแนะนำการลงทุน · ตัวเลขที่ไม่ได้ระบุว่า "สด" เป็นค่าที่รวบรวมด้วยมือ ณ ' + b.asOf + ' และเป็นค่าประมาณ · ที่มา: ',
          'Educational research, not investment advice. Figures not marked live are hand-curated as of ' + b.asOf + ' and approximate. Sources: ') + b.sources}
      </div>
    </div>
  );
}

function Dial({ label, d, fmt, sub, good, t }: {
  label: string; d: BubbleDial | null; fmt: (x: number) => string;
  sub: string | null; good: boolean; t: any;
}) {
  // A dial that could not be read says so. Rendering it as zero would, on a
  // liquidity gauge, read as "there is no money in the system".
  if (!d) {
    return (
      <div className="mm-bubble-dial off">
        <span className="dim mono mm-bubble-dlabel">{label}</span>
        <b className="dim">{t('ดึงข้อมูลไม่ได้', 'unavailable')}</b>
      </div>
    );
  }
  return (
    <div className="mm-bubble-dial">
      <span className="dim mono mm-bubble-dlabel">{label}</span>
      <b className={`mono ${good ? 'up' : ''}`}>{fmt(d.value)}</b>
      {sub && <span className="dim mm-bubble-dsub">{sub}</span>}
      <span className="dim mono mm-bubble-ddate">{t('ข้อมูล', 'as of')} {d.date} · FRED</span>
    </div>
  );
}

function Gauges({ b, t, en }: any) {
  const g = b.gauges;
  const items = [
    { k: 'cape', label: 'Shiller CAPE', val: n1(g.cape.value, 2), live: g.cape.live,
      src: (g.cape as any).source, date: (g.cape as any).asOf,
      what: t('ราคา ÷ กำไรเฉลี่ยปรับเงินเฟ้อ 10 ปี — P/E ที่เกลี่ยทั้งทศวรรษ คิดค้นโดย Robert Shiller เจ้าของรางวัลโนเบล เพื่อเทียบคำว่า "แพง" ข้ามยุคได้',
        'Price ÷ 10-year inflation-adjusted average earnings — a P/E smoothed over a decade, built by Nobel laureate Robert Shiller to compare expensive across eras.'),
      read: t('สูงเป็นอันดับ 2 ตลอดกาล — คิดเป็น 96% ของสถิติเดือน ธ.ค. 1999 (44.19) ค่าเฉลี่ยระยะยาวคือ 17.4', 'Second highest ever — 96% of the Dec 1999 record of 44.19. The long-run mean is 17.4.'),
      plain: t('คุณจ่าย $42.56 เพื่อกำไรเฉลี่ย $1 ที่ S&P 500 ทำได้ในรอบ 10 ปี (ค่าปกติ 145 ปีคือ ~$17) สองครั้งที่คนเคยจ่ายเกิน $32 คือปี 1929 กับ 1999 — ทศวรรษถัดมาผลตอบแทนแทบเป็นศูนย์',
        'You are paying $42.56 for every $1 of average inflation-adjusted profit over the past decade; the 145-year norm is ~$17. The only times buyers paid $32+ were 1929 and 1999, and the following decade returned roughly zero.') },
    { k: 'buffett', label: 'Buffett Indicator', val: n1(g.buffett.value, 0) + '%', live: g.buffett.live, date: g.buffett.date,
      what: t('มูลค่าหุ้นสหรัฐทั้งตลาด ÷ GDP สหรัฐ — วอร์เรน บัฟเฟตต์เรียกมันว่า "น่าจะเป็นมาตรวัดเดี่ยวที่ดีที่สุด" ว่าตลาดโตเกินเศรษฐกิจจริงไปแล้วหรือยัง',
        'All US stocks ÷ US GDP. Buffett called it probably the best single measure of whether the market has outgrown the real economy.'),
      read: t('ดอตคอมพีคราว 140% ตอนนี้สูงกว่าเส้นแนวโน้ม 2.1 ส่วนเบี่ยงเบนมาตรฐาน', 'The dot-com peak was near 140%. Today sits 2.1σ above trend.'),
      plain: t('ตลาดหุ้นสหรัฐทั้งตลาดมีราคาเท่ากับทุกอย่างที่เศรษฐกิจสหรัฐผลิตได้ 2.2 ปี บัฟเฟตต์เคยบอกว่าราว 200% คือ "เล่นกับไฟ"',
        'The entire US stock market costs 2.2 years of everything the economy produces. Buffett called ~200% playing with fire.') },
    { k: 'top10', label: t('น้ำหนัก 10 ตัวใหญ่ใน S&P 500', 'Top-10 weight in S&P 500'), val: g.top10Weight.value + '%', live: g.top10Weight.live,
      what: t('น้ำหนักรวมของ 10 บริษัทใหญ่สุดใน S&P 500 — มาตรวัดที่ง่ายที่สุดว่าตลาดกระจุกแค่ไหน', 'The combined index weight of the 10 largest companies — the simplest gauge of concentration.'),
      read: t('สูงกว่าทุกช่วงในประวัติศาสตร์ของดัชนี ปี 2000 พีคราว 25%', 'Higher than any point in the index\'s history; 2000 peaked near 25%.'),
      plain: t('ใส่เงิน 100 บาทในกองทุนดัชนี S&P 500 มี 42 บาทลงไปอยู่ใน 10 บริษัท และราว 9 บาทอยู่ใน Nvidia ตัวเดียว กองทุน "กระจายความเสี่ยง" จึงเป็นการเดิมพันหุ้นเทคขนาดใหญ่แบบกระจุกโดยไม่รู้ตัว',
        'Put $100 in an S&P 500 index fund and $42 lands in ten companies, about $9 in Nvidia alone. A diversified index fund is quietly a concentrated mega-cap tech bet.') },
    { k: 'ai', label: t('สัดส่วนการปรับขึ้นของตลาดที่มาจากหุ้น AI', 'Share of market gains from AI'), val: g.aiShareOfGains.value + '%', live: g.aiShareOfGains.live,
      what: t('ตลาดขึ้นมาทั้งหมดเท่าไร มาจากหุ้นกลุ่ม AI กี่เปอร์เซ็นต์ — บอกว่าตลาดกระทิงรอบนี้พึ่งพาการเดิมพันเดียวมากแค่ไหน',
        'The share of the market\'s total rise that came from AI-linked stocks alone — how much of the bull market is one trade.'),
      read: t('นับตั้งแต่ ChatGPT เปิดตัว (พ.ย. 2022) หุ้น AI ขับเคลื่อนราว 70% ของการขึ้นทั้งหมด', 'Since ChatGPT launched in Nov 2022, AI names drove ~70% of all gains.'),
      plain: t('ทุก 100 บาทที่ตลาดสหรัฐเพิ่มขึ้นตั้งแต่ พ.ย. 2022 ราว 70 บาทมาจากหุ้นสาย AI ถ้าตัดกลุ่มนี้ออก สามปีที่ผ่านมาก็ดูธรรมดามาก',
        'Of every $100 the US market gained since Nov 2022, about $70 came from AI-linked names. Strip them out and the past three years look ordinary.') },
  ];
  return (
    <div className="mm-bubble-body">
      <div className="mm-bubble-say">
        {t('สามในสี่มาตรวัดคลาสสิกอยู่ที่ระดับสูงสุดหรือใกล้สูงสุดเป็นประวัติการณ์ มีเพียงตัวเดียว — ราคาที่จ่ายต่อกำไรจริงหนึ่งดอลลาร์ของตัวนำ — ที่ยังดูเบากว่าปี 2000',
          'Three of the four classic gauges are at or near all-time records. Only one — the price paid per dollar of actual profit at the top — looks tamer than 2000.')}
      </div>
      <div className="mm-bubble-gauges">
        {items.map(it => (
          <div className="mm-bubble-gauge" key={it.k}>
            <div className="mm-bubble-grow">
              <span className="dim mono">{it.label}</span>
              <b className="mono">{it.val}</b>
            </div>
            {/* Name the source that was actually read. Every live gauge used to
                say FRED because every live gauge came from FRED; CAPE comes
                from multpl.com, and a badge that credits the wrong publisher is
                a citation error on a card whose whole argument is provenance. */}
            <span className={`mm-bubble-badge ${it.live ? 'live' : 'static'}`}>
              {it.live
                ? t('สด · ' + (it.src || 'FRED') + (it.date ? ' ' + it.date : ''),
                    'live · ' + (it.src || 'FRED') + (it.date ? ' ' + it.date : ''))
                : t('ค่าคงที่ ณ ' + b.asOf, 'as of ' + b.asOf)}
            </span>
            <div className="dim mm-bubble-what">{it.what}</div>
            <div className="mm-bubble-read">{it.read}</div>
            <div className="dim mm-bubble-plain">{it.plain}</div>
          </div>
        ))}
      </div>
      {/* The long-run scale each gauge is read against. A CAPE of 42.6 means
          nothing until you can see 17.4 and 44.2 on the same line. */}
      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{en ? b.gaugeHistory.cape.label : 'Shiller CAPE ตั้งแต่ปี 1881 ถึงวันนี้'}</div>
        <GaugeScale h={b.gaugeHistory.cape} unit="" max={50} en={en} />
        <div className="mm-bubble-needle">
          <div className="mm-bubble-needlebar">
            <i style={{ left: b.gauges.cape.percentile + '%' }} />
          </div>
          <div className="dim mm-bubble-note">
            {t('เข็มชี้: CAPE วันนี้อยู่ที่เปอร์เซ็นไทล์ที่ ' + b.gauges.cape.percentile + ' ของข้อมูล 145 ปี — แปลว่าใน 100 เดือนของประวัติศาสตร์ มีเพียง ' + (100 - b.gauges.cape.percentile) + ' เดือนที่แพงกว่านี้ (สถิติปี 1999 คือ ' + b.gauges.cape.record + ')',
              'The needle: CAPE today sits at the ' + b.gauges.cape.percentile + 'th percentile of 145 years — only ' + (100 - b.gauges.cape.percentile) + ' months in a hundred were ever more expensive. The 1999 record was ' + b.gauges.cape.record + '.')}
          </div>
        </div>
      </div>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{en ? b.gaugeHistory.buffett.label : 'Buffett Indicator ตั้งแต่ปี 1970 ถึงวันนี้'}</div>
        <GaugeScale h={b.gaugeHistory.buffett} unit="%" max={240} en={en} />
      </div>

      <div className="mm-bubble-say">
        {t('"แพงเกินตัว" คือแพงแค่ไหนเมื่อเทียบกับกำไรและเศรษฐกิจที่ต้องมารองรับ เหมือนหนังยาง มาตรวัดมูลค่าไม่เคยบอกว่ามันจะดีดกลับเมื่อไหร่ บอกได้แค่ว่าจะแรงแค่ไหน · วันนี้ CAPE อยู่ที่เปอร์เซ็นไทล์ที่ 98 ของข้อมูล 145 ปี',
          'Stretched means expensive relative to the profits and economy that must support it — like a rubber band. Valuation never tells you when it snaps, only how hard. Today\'s CAPE sits at the 98th percentile of 145 years.')}
      </div>
    </div>
  );
}

function Concentration({ b, t, en }: any) {
  const max = Math.max(...b.concentration.map((c: any) => c.peak));
  return (
    <div className="mm-bubble-body">
      <div className="mm-bubble-say">
        {t('S&P 500 มีหุ้น 500 ตัว แต่ 10 ตัวคือ 42% ของเงินทั้งหมด นักวิจัยที่ BofA และ Bianco Research เรียกมันว่าตลาดสหรัฐที่กระจุกตัวที่สุดในรอบศตวรรษ',
          'The S&P 500 is 500 stocks, but 10 of them are 42% of the money — the most concentrated US market in a century.')}
      </div>
      {b.concentration.map((c: any) => (
        <div className={`mm-bubble-era${c.current ? ' now' : ''}`} key={c.theme}>
          <div className="mm-bubble-erahead">
            <div>
              <b>{en ? c.theme : c.th}</b>
              <span className="dim mono"> · {c.era} · {c.names}</span>
            </div>
            <b className={`mono ${c.current ? 'gold' : ''}`}>{c.peak}%</b>
          </div>
          <div className="mm-bubble-erabar">
            <i className={c.current ? 'now' : ''} style={{ width: Math.round(c.peak / max * 100) + '%' }} />
          </div>
          <div className="dim mm-bubble-what">{en ? c.what.en : c.what.th}</div>
          {/* The names are the argument. A drawdown number is abstract; Polaroid
              -91%, Disney -82%, Coca-Cola -69% is what a 40% concentration peak
              did to people who held the safest stocks of their generation. */}
          {c.casualties && (
            <div className="mm-bubble-cas mono">
              {c.casualties.map((x: any) => (
                <span key={x.n}>
                  {x.n} <b className={String(x.f).startsWith('-') || /ล้ม|สูญ|failed|bankrupt|wiped/.test(en && x.fEn ? x.fEn : x.f) ? 'down' : 'dim'}>
                    {en && x.fEn ? x.fEn : x.f}
                  </b>
                </span>
              ))}
            </div>
          )}
          <div className={`mm-bubble-after ${c.current ? 'gold' : 'down'}`}>→ {en ? c.after.en : c.after.th}</div>
        </div>
      ))}
      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('น้ำหนัก 10 ตัวใหญ่ใน S&P 500 ตั้งแต่ปี 1980', 'Top-10 share of S&P 500, 1980 → today')}</div>
        <GaugeScale h={b.gaugeHistory.top10} unit="%" max={50} en={en} />
      </div>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('ดัชนีเดียว แต่เป็นสองตลาด', 'One index, two markets')}</div>
        <div className="mm-bubble-split">
          <div className="mm-bubble-splitbar">
            <i className="top" style={{ width: b.indexSplit.top10 + '%' }} />
            <i className="rest" style={{ width: b.indexSplit.rest + '%' }} />
          </div>
          <div className="mm-bubble-splitlabels mono">
            <span className="gold">{t('หุ้น 10 ตัวใหญ่', 'Top 10 stocks')} <b>{b.indexSplit.top10}%</b></span>
            <span className="dim">{t('อีก ' + b.indexSplit.restCount + ' ตัวที่เหลือ', 'The other ' + b.indexSplit.restCount)} <b>{b.indexSplit.rest}%</b></span>
          </div>
        </div>
        <div className="dim mm-bubble-note">
          {t('เมื่อผู้นำตลาดแคบขนาดนี้ คนที่ซื้อกองทุนดัชนีก็กำลังเดิมพันกับหุ้น AI ไม่กี่ตัว ไม่ว่าจะรู้ตัวหรือไม่',
            'When leadership is this narrow, index investors own a bet on a handful of AI names whether they know it or not.')}
        </div>
      </div>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('หุ้นยักษ์ตามมูลค่าตลาด ($ ล้านล้าน)', 'The mega-caps, by market cap ($T)')}</div>
        <div className="mm-bubble-mcwrap">
          <table className="mm-table mono mm-bubble-mc">
            <thead><tr><th>{t('หุ้น', 'stock')}</th><th>{t('มูลค่าตลาด', 'market cap')}</th><th>P/E</th></tr></thead>
            <tbody>
              {b.megacaps.map((m: any) => (
                <tr key={m.sym}>
                  <td><b>{m.sym}</b> <span className="dim">{m.name}</span></td>
                  <td>${n1(m.cap, 2)}T</td>
                  <td className={m.pe > 60 ? 'down' : ''}>{m.pe}×</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Read from the same rows as the table directly above. These were
            hardcoded at $5.45T and $23.5T while the table beside them went
            live from Finnhub, so the caption could state one number while
            the row above stated another - today it reads NVDA 5.42T. The
            share-of-index figure is gone: no S&P total cap is served here,
            and a percentage nobody can check against the page is exactly
            the kind of number this panel exists to distrust. Rendered only
            when the live rows are present. */}
        {(() => {
          const caps = (b.megacaps || []).filter((m: any) => typeof m.cap === 'number' && m.cap > 0);
          const nvda = caps.find((m: any) => m.sym === 'NVDA');
          if (!nvda || caps.length < 5) return null;
          const nv = n1(nvda.cap, 2);
          const tot = n1(caps.reduce((s: number, m: any) => s + m.cap, 0), 1);
          const n = caps.length;
          return (
            <div className="dim mm-bubble-note">
              {t(
                `Nvidia ตัวเดียว (${nv}T) คือน้ำหนักหุ้นเดี่ยวที่สูงที่สุดเท่าที่เคยบันทึกไว้ใน S&P 500 ส่วนยักษ์ ${n} ตัวข้างบนรวมกัน ${tot}T`,
                `Nvidia alone (${nv}T) is the largest single-stock weight ever recorded in the S&P 500. The ${n} giants listed above come to ${tot}T together.`)}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function CrashHistory({ b, t, en }: any) {
  const worst = Math.max(...b.crashes.map((c: any) => Math.abs(c.dd)));
  return (
    <div className="mm-bubble-body">
      <AlignedChart a={b.aligned} t={t} en={en} />

      <div className="mm-bubble-say">
        {t('เจ็ดครั้งในหนึ่งศตวรรษที่ตลาดสหรัฐเสียมูลค่าไปหนึ่งในสี่หรือมากกว่า รูปแบบซ้ำเดิม — ยิ่งเริ่มต้นที่มูลค่าแพงเท่าไร ยิ่งร่วงลึกและใช้เวลากลับนานเท่านั้น',
          'Seven times in a century the US market lost a quarter or more. The higher the starting valuation, the deeper the fall and the longer the climb back.')}
      </div>
      {b.crashes.map((c: any) => (
        <div className="mm-bubble-crash" key={c.name}>
          <div className="mm-bubble-crashhead">
            <b>{en ? c.name : c.th}</b>
            <b className="mono down">{c.dd}%</b>
          </div>
          <div className="mm-bubble-erabar"><i className="dd" style={{ width: Math.round(Math.abs(c.dd) / worst * 100) + '%' }} /></div>
          <div className="mm-bubble-crashmeta mono dim">
            <span>{t('จากจุดสูงสุดถึงจุดต่ำสุด', 'peak to trough')} {c.months} {t('เดือน', 'mo')}</span>
            <span>{t('ใช้เวลากลับไปจุดเดิม', 'recovery')} {en ? c.recoveryEn : c.recovery}</span>
          </div>
          <div className="dim mm-bubble-what">{en ? c.note.en : c.note.th}</div>
        </div>
      ))}
      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('CAPE ณ จุดสูงสุดของทุกรอบใหญ่', 'CAPE at every major peak')}</div>
        <div className="mm-bubble-peaks mono">
          {b.capeAtPeaks.map((p: any) => (
            <span key={p.y} className={p.current ? 'gold' : ''}>
              <span className="dim">{p.y}</span> <b>{p.v}</b>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function IpoTell({ b, t, en }: any) {
  const maxV = Math.max(...b.ipoProceeds.map((p: any) => p.v));
  return (
    <div className="mm-bubble-body">
      <div className="mm-bubble-say">
        {t('หนึ่งในสัญญาณที่เชื่อถือได้ที่สุดคือ IPO ยักษ์ — ดีลใหญ่ที่สุดและร้อนแรงที่สุดของยุค มักเข้าตลาดภายในไม่กี่เดือนจากจุดสูงสุด เพราะความต้องการซื้อกระดาษที่สูงเป็นประวัติการณ์ คือนิยามของจุดสูงสุดนั่นเอง',
          'One of the most reliable tells is the mega-IPO: the era\'s biggest, hottest listing almost always arrives within months of the peak, because record demand for paper is exactly what a top is.')}
      </div>
      {b.ipoTell.map((g: any) => (
        <div className={`mm-bubble-ipogrp${g.current ? ' now' : ''}`} key={g.crisis}>
          <div className="mm-bubble-ipohead">
            <b>{g.crisis}</b>
            <span className="dim mono">{t('พีค', 'peak')} {g.peak}</span>
          </div>
          <div className="mm-bubble-ipowrap">
            <table className="mm-table mono mm-bubble-ipo">
              <thead><tr>
                <th>IPO</th><th>{t('เข้าตลาด', 'listed')}</th>
                <th>{t('ห่างจากพีค', 'IPO → peak')}</th><th>{t('ผลลัพธ์', 'fate')}</th>
              </tr></thead>
              <tbody>
                {g.rows.map((r: any) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td className="dim">{r.date}</td>
                    <td>{en ? r.leadEn : r.lead}</td>
                    <td className={String(r.fate).startsWith('-') || /ล้ม|สูญ|อื้อฉาว/.test(r.fate) ? 'down' : 'dim'}>{r.fate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('เงินที่ระดมได้จาก IPO สหรัฐต่อปี ($ พันล้าน)', 'Total raised in US IPOs per year ($B)')}</div>
        <div className="mm-bubble-ipobars">
          {b.ipoProceeds.map((p: any) => (
            <div className="mm-bubble-ipobar" key={p.y} title={p.y + ': $' + p.v + 'B'}>
              <i style={{ height: Math.max(3, Math.round(p.v / maxV * 100)) + '%' }} className={p.est ? 'est' : ''} />
              <span className="dim mono">{String(p.y).slice(2)}</span>
            </div>
          ))}
        </div>
        <div className="dim mm-bubble-note">
          {t('เงินบอกเรื่องเดียวกัน — ยอด IPO พุ่งพอดีที่จุดสูงสุดของตลาด (1999–2000, 2021) แล้วพังในปีถัดมา ปี 2026 กำลังมุ่งไปสู่ปีที่ IPO ใหญ่ที่สุดนับจาก 2021 (แถบจาง = ตัวเลขประมาณการ)',
            'The money tells the same story: IPO proceeds spike exactly at tops — 1999–2000, 2021 — and collapse the year after. 2026 is tracking toward the biggest IPO year since 2021 (faded bars are estimates).')}
        </div>
      </div>
    </div>
  );
}

function Anatomy({ b, t, en }: any) {
  const maxC = Math.max(...b.hyperscalerCapex.map((c: any) => c.v));
  return (
    <div className="mm-bubble-body">
      <div className="mm-bubble-say">
        {t('คนละทศวรรษ แต่สูตรเดียวกัน ตลอดสองศตวรรษ ส่วนผสมห้าอย่างนี้โผล่มาที่จุดสูงสุดทุกครั้ง',
          'Different decade, same recipe. Across two centuries the same five ingredients show up at every top.')}
      </div>
      <ol className="mm-bubble-ing">
        {b.ingredients.map((x: any, i: number) => (
          <li key={i}><b>{en ? x.en : x.th}</b> <span className="dim">— {en ? x.d.en : x.d.th}</span></li>
        ))}
      </ol>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('เงินลงทุนดาต้าเซ็นเตอร์ของ 4 ยักษ์คลาวด์ ($ พันล้าน/ปี)', 'Hyperscaler capex, MSFT + GOOGL + AMZN + META ($B/yr)')}</div>
        <div className="mm-bubble-ipobars">
          {b.hyperscalerCapex.map((c: any) => (
            <div className="mm-bubble-ipobar" key={c.y} title={c.y + ': $' + c.v + 'B'}>
              <i style={{ height: Math.max(3, Math.round(c.v / maxC * 100)) + '%' }} className={c.est ? 'est' : ''} />
              <span className="dim mono">{String(c.y).slice(2)}</span>
            </div>
          ))}
        </div>
        <div className="dim mm-bubble-note">
          {t('การลงทุน AI เป็นเงินจริงกว่าครึ่งล้านล้านดอลลาร์ต่อปี ซึ่งเป็นเหตุผลว่าทำไมกรณีพังถึงสำคัญ — บูมการลงทุนจบด้วยกำลังการผลิตล้นเสมอ',
            'The AI build-out is real spending, over half a trillion a year — which is exactly why the crash case matters: capex booms end in capex busts.')}
        </div>
      </div>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('สี่ระยะของฟองสบู่ (Minsky / Rodrigue) — ลองถามตัวเองตรง ๆ ว่าวันนี้ใกล้ป้ายไหนที่สุด', 'The four phases (Minsky / Rodrigue) — ask honestly which label is closest to today')}</div>
        <div className="mm-bubble-phases">
          {b.phases.map((p: any, i: number) => (
            <div className="mm-bubble-phase" key={i}>
              <b>{i + 1}. {en ? p.en : p.th}</b>
              <span className="dim">{en ? p.sub.en : p.sub.th}</span>
            </div>
          ))}
        </div>
        <div className="dim mm-bubble-note">
          {t('"คราวนี้ไม่เหมือนเดิม" ถูกพูดที่ทุกจุดสูงสุดตั้งแต่ปี 1720 — บางครั้งก็จริง แต่ส่วนใหญ่ไม่',
            '\'This time is different\' has been said at every peak since 1720. Sometimes it is; usually it isn\'t.')}
        </div>
      </div>

      <div className="mm-bubble-sub">
        <div className="dim mm-bubble-subt">{t('ดอตคอม 2000 เทียบ AI 2026', 'Dot-com 2000 versus AI 2026')}</div>
        <div className="mm-bubble-mcwrap">
          <table className="mm-table mono mm-bubble-mc">
            <thead><tr><th></th><th>{t('ดอตคอม 2000', 'Dot-com 2000')}</th><th>{t('AI 2026', 'AI 2026')}</th></tr></thead>
            <tbody>
              {b.thenVsNow.map((r: any) => (
                <tr key={r.metric.en}>
                  <td className="dim">{en ? r.metric.en : r.metric.th}</td>
                  <td>{en && r.thenEn ? r.thenEn : r.then}</td>
                  <td><b>{en && r.nowEn ? r.nowEn : r.now}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="dim mm-bubble-note">
          {t('2026 ไม่ใช่สำเนาของปี 2000 — ยักษ์ใหญ่วันนี้ทำกำไรจริงมหาศาล แต่ความกระจุกตัวและราคาของทั้งตลาดเลยจุดสุดขั้วทุกครั้งในประวัติศาสตร์ไปแล้ว',
            '2026 is not a copy of 2000: today\'s giants earn enormous real profits. But concentration and the price of the whole market are past every historical extreme.')}
        </div>
      </div>

      <ul className="mm-bubble-take">
        {b.takeaways.map((x: any, i: number) => <li key={i}>{en ? x.en : x.th}</li>)}
      </ul>
    </div>
  );
}

// A single long-run scale with the historical marks placed on it. A gauge
// reading means nothing on its own: 42.6 is only alarming once the 145-year
// mean of 17.4 and the 1999 record of 44.2 sit on the same line beside it.
function GaugeScale({ h, unit, max, en }: { h: any; unit: string; max: number; en: boolean }) {
  return (
    <div className="mm-bubble-scale">
      <div className="mm-bubble-scaleline">
        {h.marks.map((m: any) => (
          <i key={m.kEn} className={m.current ? "now" : ""} style={{ left: Math.min(99, m.v / max * 100) + "%" }} />
        ))}
      </div>
      <div className="mm-bubble-scalemarks mono">
        {h.marks.map((m: any) => (
          <span key={m.kEn} className={m.current ? "gold" : "dim"}>
            <b>{m.v}{unit}</b> {en ? m.kEn : m.k}
          </span>
        ))}
      </div>
    </div>
  );
}

// Every great bubble rescaled so its own top equals 100, then laid on one
// clock. Drawn as an inline SVG from the study's points rather than embedding
// its image, so it inherits the site theme and stays legible on a phone.
function AlignedChart({ a, t, en }: { a: any; t: any; en: boolean }) {
  const W = 640, H = 210, PL = 34, PR = 10, PT = 12, PB = 24;
  const xs = a.series.flatMap((s: any) => s.points.map((p: any) => p[0]));
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const X = (v: number) => PL + (v - x0) / (x1 - x0) * (W - PL - PR);
  const Y = (v: number) => PT + (1 - v / 110) * (H - PT - PB);
  const COLORS: Record<string, string> = {
    dow1929: "#e0525f", nikkei1989: "#f0b90b", nasdaq2000: "#7ab8ff", spxNow: "#26a17b",
  };
  return (
    <div className="mm-bubble-sub">
      <div className="dim mm-bubble-subt">
        {t("ฟองสบู่ครั้งใหญ่ทุกครั้ง จัดให้จุดสูงสุดตรงกัน (พีค = 100)", "Every great bubble, aligned at its peak (peak = 100)")}
      </div>
      <div className="mm-bubble-svgwrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="mm-bubble-svg" role="img"
             aria-label={en ? "Bubbles aligned at their peaks" : "ฟองสบู่จัดให้จุดสูงสุดตรงกัน"}>
          <line x1={X(0)} y1={PT} x2={X(0)} y2={H - PB} className="mm-bubble-axis peak" />
          {[0, 25, 50, 75, 100].map(v => (
            <g key={v}>
              <line x1={PL} y1={Y(v)} x2={W - PR} y2={Y(v)} className="mm-bubble-grid" />
              <text x={PL - 6} y={Y(v) + 3} className="mm-bubble-tick" textAnchor="end">{v}</text>
            </g>
          ))}
          {[-5, -3, -1, 0, 1, 2, 3].filter(v => v >= x0 && v <= x1).map(v => (
            <text key={v} x={X(v)} y={H - 8} className="mm-bubble-tick" textAnchor="middle">
              {v === 0 ? (en ? "peak" : "พีค") : (v > 0 ? "+" + v : String(v))}
            </text>
          ))}
          {a.series.map((s: any) => (
            <polyline key={s.key} fill="none" strokeWidth={s.current ? 2.4 : 1.5}
              stroke={COLORS[s.key] || "#888"}
              strokeDasharray={s.current ? undefined : "0"}
              opacity={s.current ? 1 : 0.72}
              points={s.points.map((p: any) => X(p[0]) + "," + Y(p[1])).join(" ")} />
          ))}
          {a.series.filter((s: any) => s.current).map((s: any) => {
            const lastP = s.points[s.points.length - 1];
            return <circle key={s.key} cx={X(lastP[0])} cy={Y(lastP[1])} r={3.4} fill={COLORS[s.key]} />;
          })}
        </svg>
      </div>
      <div className="mm-bubble-legend mono">
        {a.series.map((s: any) => (
          <span key={s.key}>
            <i style={{ background: COLORS[s.key] }} />{s.label}{s.current ? " ←" : ""}
          </span>
        ))}
      </div>
      <div className="dim mm-bubble-note">{en ? a.note.en : a.note.th}</div>
      <div className="mm-bubble-after gold">{en ? a.today.en : a.today.th}</div>
    </div>
  );
}

// What a non-paying reader sees behind the blur: the structure of the study and
// what it covers, with no figures in it. The point of MemberGate is that the
// card stays visible enough to be worth subscribing for - but the numbers are
// the product, so none of them appear here. Nothing in this component is
// fetched; it cannot leak what the server just refused to send.
function BubbleTeaser({ t, anon }: { t: any; anon: boolean }) {
  const rows = [
    t("มาตรวัดฟองสบู่ 4 ตัว — Shiller CAPE, Buffett Indicator, ความกระจุกตัว 10 ตัวใหญ่ และสัดส่วนกำไรที่มาจากหุ้น AI",
      "Four bubble gauges — Shiller CAPE, the Buffett Indicator, top-10 concentration, and the AI share of market gains"),
    t("ฟองสบู่ 6 ครั้งใน 200 ปี พร้อมรายชื่อหุ้นที่พังและตัวเลขที่ร่วงจริงรายตัว",
      "Six manias across two centuries, with the names that broke and how far each fell"),
    t("วิกฤตใหญ่ 7 ครั้งในหนึ่งศตวรรษ — ร่วงลึกแค่ไหน กี่เดือน และใช้เวลากลับมานานเท่าไร",
      "Seven major crashes in a century — how deep, how long, and how many years back"),
    t("สัญญาณ IPO ยักษ์ 19 ดีลใน 7 ยุค ที่เข้าตลาดก่อนจุดสูงสุดไม่กี่เดือน",
      "The mega-IPO tell: 19 landmark listings that hit the tape months before the top"),
    t("สภาพคล่องดอลลาร์แบบสดจาก FRED — M2, ดอกเบี้ย Fed, งบดุล และเงินฝากข้ามคืน",
      "Live USD liquidity from FRED — M2, the funds rate, the balance sheet and overnight parking"),
  ];
  return (
    <div className="mm-bubble">
      <div className="mm-bubble-head">
        <div>
          <b>{t("Bubble Watch · เฝ้าระวังฟองสบู่", "Bubble Watch")}</b>
          <div className="dim mm-bubble-lede">
            {t("วิกฤตตลาดสหรัฐทุกครั้งในหน้าเดียว — ความกระจุกตัว มูลค่า และระดับความคลั่ง เทียบกับหนึ่งศตวรรษของการพังทลาย",
              "Every US market crisis on one page — concentration, valuation and mania gauges against a century of crashes.")}
          </div>
        </div>
      </div>
      <ul className="mm-bubble-teaser">
        {rows.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
      <div className="dim mm-bubble-foot">
        {anon
          ? t("เข้าสู่ระบบเพื่อดูงานวิจัยฉบับเต็ม", "Sign in to read the full study")
          : t("งานวิจัยฉบับเต็มสำหรับสมาชิก Gold และ Premium", "The full study is for Gold and Premium members")}
      </div>
    </div>
  );
}
